import { z } from "zod";

import type { FilterDefinition } from "./FilterDefinition";

export type FilterDataType = "string" | "numeric" | "date" | "boolean" | "enum";

export const stringComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "LIKE",
  "NOT_LIKE",
] as const;

export const numericComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
] as const;

export const dateComparators = [
  "LESS_THAN",
  "GREATER_THAN",
  "BETWEEN",
] as const;

export const booleanComparators = ["EQUALS", "EXISTS", "NOT_EXISTS"] as const;

export const enumComparators = ["CONTAINS", "NOT_CONTAINS"] as const;

/**
 * Preset comparators are a valueless UI convenience.
 */
export const datePresets = [
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_90_DAYS",
  "THIS_MONTH",
] as const;

export const comparatorsByType = {
  string: stringComparators,
  numeric: numericComparators,
  date: dateComparators,
  boolean: booleanComparators,
  enum: enumComparators,
} as const satisfies Record<FilterDataType, readonly string[]>;

export const presetsByType = {
  string: [],
  numeric: [],
  date: datePresets,
  boolean: [],
  enum: [],
} as const satisfies Record<FilterDataType, readonly string[]>;

type ComparatorsFor<T extends FilterDataType> =
  | (typeof comparatorsByType)[T][number]
  | (typeof presetsByType)[T][number];

export type StringComparator = ComparatorsFor<"string">;
export type NumericComparator = ComparatorsFor<"numeric">;
export type DateComparator = ComparatorsFor<"date">;
export type BooleanComparator = ComparatorsFor<"boolean">;
export type EnumComparator = ComparatorsFor<"enum">;

export type FilterComparator = ComparatorsFor<FilterDataType>;

/**
 * Subtype of `DateComparator` corresponding to the relative presets — the
 * narrow form `filterToCondition` accepts when expanding to a live
 * `now`-relative range.
 */
export type DatePresetComparator = (typeof datePresets)[number];

/**
 * All preset comparators across every field type — derived from
 * `presetsByType` so adding/reordering presets only touches one place.
 * Presets are valueless and resolved at query-emit time (e.g. date
 * relative presets expand to a `now`-relative range inside
 * `filterToCondition` so a saved filter stays live across days).
 */
export const PRESET_COMPARATORS: ReadonlySet<FilterComparator> = new Set(
  Object.values(presetsByType).flat(),
);

/**
 * Comparators that render no value input — the comparator alone carries
 * the semantic. Today this is `EXISTS` / `NOT_EXISTS` (boolean's
 * three-state pattern) plus every preset.
 */
export const VALUELESS_COMPARATORS: ReadonlySet<FilterComparator> = new Set([
  "EXISTS",
  "NOT_EXISTS",
  ...PRESET_COMPARATORS,
]);

export function isPreset(comparator: FilterComparator): boolean {
  return PRESET_COMPARATORS.has(comparator);
}

export function isValueless(comparator: FilterComparator): boolean {
  return VALUELESS_COMPARATORS.has(comparator);
}

/**
 * Narrow guard used by `filterToCondition` to dispatch into the
 * date-specific preset→range expander. Distinct from the generic
 * `isPreset` because preset expansion logic is type-specific (today only
 * dates) and needs the narrowed `DatePresetComparator` for exhaustiveness.
 */
export function isDatePreset(
  comparator: FilterComparator,
): comparator is DatePresetComparator {
  return (datePresets as readonly string[]).includes(comparator);
}

const stringVariant = z.object({
  type: z.literal("string"),
  field: z.string().min(1),
  comparator: z.enum(stringComparators),
  values: z.array(z.string()),
});

const numericVariant = z.object({
  type: z.literal("numeric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
});

const dateVariant = z.object({
  type: z.literal("date"),
  field: z.string().min(1),
  comparator: z.enum([...dateComparators, ...datePresets]),
  values: z.array(z.string()),
});

const booleanVariant = z.object({
  type: z.literal("boolean"),
  field: z.string().min(1),
  comparator: z.enum(booleanComparators),
  values: z.array(z.boolean()),
});

const enumVariant = z.object({
  type: z.literal("enum"),
  field: z.string().min(1),
  comparator: z.enum(enumComparators),
  values: z.array(z.string()),
});

export const filterSchema = z
  .discriminatedUnion("type", [
    stringVariant,
    numericVariant,
    dateVariant,
    booleanVariant,
    enumVariant,
  ])
  .superRefine((data, ctx) => {
    switch (data.type) {
      case "string":
        if (
          data.values.length === 0 ||
          !data.values.some((v) => v.trim().length > 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "numeric":
        if (
          data.values.length === 0 ||
          !data.values.every((n) => Number.isFinite(n))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "date":
        // Relative presets are valueless — the comparator alone carries
        // the semantic.
        if (isDatePreset(data.comparator)) {
          return;
        }
        if (data.comparator === "BETWEEN") {
          if (
            data.values.length !== 2 ||
            data.values.some((v) => v.length === 0)
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "Start and end dates required",
            });
          } else if (data.values[0] > data.values[1]) {
            // ISO 8601 strings (UTC midnight) compare lexicographically,
            // so plain `>` is a correct date ordering check here.
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "End date must be on or after start date",
            });
          }
        } else if (data.values.length === 0 || data.values[0].length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "boolean":
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.values.length !== 1) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick true or false",
          });
        }
        return;
      case "enum":
        if (data.values.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
    }
  });

/**
 * The canonical UI Filter shape
 */
export type Filter = z.infer<typeof filterSchema>;

/**
 * Defaults seeded into `useForm` when the user picks a field. Empty
 * `values` array — the refine will fail until the user fills something
 * in, which keeps Add filter disabled. Date defaults to LESS_THAN
 * ("before"); the rest default to EQUALS.
 */
export function getDefaultsForVariant(definition: FilterDefinition): Filter {
  switch (definition.type) {
    case "string":
      return {
        type: "string",
        field: definition.field,
        comparator: "EQUALS",
        values: [],
      };
    case "numeric":
      return {
        type: "numeric",
        field: definition.field,
        comparator: "EQUALS",
        values: [],
      };
    case "date":
      return {
        type: "date",
        field: definition.field,
        comparator: "LESS_THAN",
        values: [],
      };
    case "boolean":
      return {
        type: "boolean",
        field: definition.field,
        comparator: "EQUALS",
        values: [],
      };
    case "enum":
      return {
        type: "enum",
        field: definition.field,
        comparator: "CONTAINS",
        values: [],
      };
  }
}
